import MobileTransactionItem from 'app/pages/mobile/pages/mobile-transactions/MobileTransactionItem';
import MobileTransactionDetails from 'app/pages/mobile/pages/mobile-transactions/MobileTransactionDetails';
import MobileEditTransactionSheet from 'app/pages/mobile/pages/mobile-transactions/MobileEditTransactionSheet';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { selectUserCompanies } from 'app/store/userCompaniesSlice';
import { useTransactionSheets } from 'app/pages/mobile/hooks/useTransactionSheets';
import { useCallback, useEffect, useState, useMemo } from 'react';
import type { HighnoteTransactionEvent } from 'API';
import { useAppDispatch, useAppSelector } from 'app/store';
import { Box, Typography } from '@mui/material';
import MobileTransactionTabs from 'app/pages/mobile/components/MobileTransactionTabs';
import MobileSearchBar from 'app/pages/mobile/components/MobileSearchBar';
import {
  selectMobileActionsHadErrorLoading,
  selectMobileActionsTransactions,
  getMobileActionsTransactions,
} from 'app/pages/store/transactionsSlice';
import { useFuelCapture } from 'app/hooks/useFuelCapture';
import { isReturned } from 'app/pages/overview/tabs/transactions/ReturnedStatusChip';
import { needsCardholderAction } from 'app/pages/mobile/utils/mobileActions';

const MobileActions = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const transactions = useAppSelector(selectMobileActionsTransactions);
  // SEE-1392: distinguish "nothing needs your attention" from "the fetch failed".
  const hadErrorLoading = useAppSelector(selectMobileActionsHadErrorLoading);
  const { selectedPaidolId, highnotePaymentCardIds, selectedPaidol } = useAppSelector(selectUserCompanies);
  const location = useLocation();
  const pageName = location.pathname;

  const [searchValue, setSearchValue] = useState('');
  const [tab, setTab] = useState<'all' | 'returned'>('all');

  const { isFuelCaptureEnabled, checkMissingFields } = useFuelCapture();

  const filteredTransactions = useMemo(() => {
    const isMissingFuelData = (transaction: HighnoteTransactionEvent) => {
      if (!isFuelCaptureEnabled) return false;
      const { isFuelTransaction, missingVehicleId, missingGallons, missingOdometer } = checkMissingFields({
        merchantCategoryCode: transaction.merchantCategoryCode,
        vehicleId: transaction.fuelVehicleId,
        gallons: transaction.fuelGallons,
        odometerReading: transaction.fuelOdometerReading,
      });
      return isFuelTransaction && (missingVehicleId || missingGallons || missingOdometer);
    };

    let filtered = transactions.filter((transaction) =>
      needsCardholderAction(transaction, selectedPaidol, isMissingFuelData(transaction))
    );

    // Apply search filter
    if (searchValue.trim()) {
      const search = searchValue.toLowerCase();
      filtered = filtered.filter((transaction) => {
        const merchantName = transaction.merchantName?.toLowerCase() || '';
        const merchantCategory = transaction.merchantCategory?.toLowerCase() || '';
        const amount = transaction.approvedAmount?.value?.toString() || '';
        const date = transaction.eventDate
          ? new Date(transaction.eventDate).toLocaleDateString().toLowerCase()
          : '';

        return (
          merchantName.includes(search) ||
          merchantCategory.includes(search) ||
          amount.includes(search) ||
          date.includes(search)
        );
      });
    }

    return filtered;
  }, [transactions, searchValue, selectedPaidol, isFuelCaptureEnabled, checkMissingFields]);

  // "All" shows every actionable transaction (returned ones included); "Returned"
  // narrows to just the returned ones.
  const displayedTransactions = useMemo(
    () =>
      tab === 'returned'
        ? filteredTransactions.filter((transaction) => isReturned(transaction.returnedStatus))
        : filteredTransactions,
    [tab, filteredTransactions]
  );

  const refetchMobileActions = useCallback(() => {
    if (selectedPaidolId && highnotePaymentCardIds.length > 0) {
      dispatch(
        getMobileActionsTransactions({
          filterObject: {
            paidolId: { eq: selectedPaidolId },
            or: highnotePaymentCardIds.map((cardId) => ({
              paymentCardId: { eq: cardId },
            })),
            mobileActions: { eq: true },
          } as any,
          limit: 10000,
        })
      );
    }
  }, [dispatch, selectedPaidolId, highnotePaymentCardIds]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(refetchMobileActions, [refetchMobileActions, pageName]);

  const {
    isTransactionDetailsOpen,
    isEditTransactionOpen,
    selectedTransaction,
    handleTransactionClick,
    handleCloseTransactionDetails,
    handleOpenEditTransaction,
    handleCloseEditTransaction,
  } = useTransactionSheets({ transactions: displayedTransactions, defaultSheet: 'edit' });

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        mt: 2,
        width: '100%',
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      {/* Actions Header - fixed at top */}
      <Box
        sx={{
          flexShrink: 0,
          bgcolor: 'background.paper',
        }}
      >
        {/* Actions title + counter */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2, mx: 1 }}>
          <Typography variant="h4" sx={{ color: 'text.primary', fontWeight: 600, letterSpacing: '0.5px' }}>
            {t('mobileActions')}
          </Typography>
          {/* Material 3 "Large" badge — #B30000 key-error pill (Figma 5518:112189) */}
          <Box
            sx={{
              minWidth: 20,
              maxWidth: 34,
              height: 16,
              px: '5px',
              borderRadius: '100px',
              bgcolor: '#B30000',
              color: 'common.white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '11px',
              fontWeight: 500,
              lineHeight: 1,
            }}
          >
            {filteredTransactions.length}
          </Box>
        </Box>

        {/* All / Returned tabs */}
        <MobileTransactionTabs value={tab} onChange={setTab} />

        {/* Search bar */}
        <MobileSearchBar value={searchValue} onChange={setSearchValue} />
      </Box>

      {/* Actions Content */}

      {/* Scrollable content area */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          '&::-webkit-scrollbar': { display: 'none' },
          scrollbarWidth: 'none',
        }}
      >
        {displayedTransactions.length === 0 && (
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mt: 2,
            }}
          >
            <Typography
              variant="large"
              sx={{
                color: hadErrorLoading ? 'error.main' : 'grey.500',
                textAlign: 'center',
                fontStyle: 'italic',
                fontWeight: 300,
              }}
            >
              {hadErrorLoading
                ? t('mobileTransactionsLoadError')
                : tab === 'returned'
                ? t('mobileNoReturnedTransactions', 'You have no returned transactions')
                : t('mobileEmptyState')}
            </Typography>
          </Box>
        )}
        {/* Transactions list */}
        {displayedTransactions.length > 0 && (
          <Box sx={{ my: 2, bgcolor: 'neutral95.main', borderRadius: 2, overflow: 'hidden' }}>
            {displayedTransactions.map((transaction) => (
              <MobileTransactionItem
                key={transaction.id}
                transaction={transaction}
                onTransactionClick={handleTransactionClick}
              />
            ))}
          </Box>
        )}
      </Box>

      {/* Transaction details */}
      <MobileTransactionDetails
        open={isTransactionDetailsOpen}
        onClose={handleCloseTransactionDetails}
        onEdit={() => {
          handleCloseTransactionDetails();
          handleOpenEditTransaction();
        }}
        transaction={selectedTransaction}
      />

      {/* Edit transaction */}
      <MobileEditTransactionSheet
        open={isEditTransactionOpen}
        onClose={handleCloseEditTransaction}
        transaction={selectedTransaction}
        onSaved={refetchMobileActions}
      />
    </Box>
  );
};

export default MobileActions;
